import React, { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import { Link, useHistory } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData, postData, postDataN } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { FixedSizeList as List } from 'react-window';
import axios from 'axios';
import { Tooltip } from '@mui/material';

function ItemIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [fileData, setFileData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const history = useHistory();
    const { isAuthenticated, companyId, branchId, userId, permissions, roleName, token, logout } = useAuth();

    const hasCreateMasterPermission = permissions.includes('Create Master');
    const hasEditMasterPermission = permissions.includes('Edit Master');
    const hasDeleteMasterPermission = permissions.includes('Delete Master');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }

        fetchtableData();
    }, [isAuthenticated, companyId, branchId, roleName, token, logout]);

    const fetchtableData = async () => {
        fetchData('ItemApi', `/${companyId}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching Item Group Details:', error);
                if (error.response && error.response.status === 401) {
                    logout();
                    history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
                }
            });
    };

    const handleDelete = async () => {
        deleteData('ItemApi', `${deleteItemId}`, token)
            .then((response) => {
                fetchtableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('ItemApi', `${itemId}`, token)
            .then(() => {
                fetchtableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    const openImportDialog = () => {
        setImportDialogOpen(true);
    };

    const closeImportDialog = () => {
        setImportDialogOpen(false);
        setFileData([]);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const reader = new FileReader();

        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            const rowsToRead = sheetData.slice(1);

            const formattedData = rowsToRead
                .filter(row => row[2])
                .map((row) => {
                    return {
                        Material: row[0] ? String(row[0]) : "",
                        Description: row[1] ? String(row[1]) : "",
                        PG: row[2] ? String(row[2]) : "",
                        IG: row[3] ? String(row[3]) : "",
                        ISG: String(row[4]),
                        PF: row[5] ? String(row[5]) : "",
                        Group: row[6] ? String(row[6]) : "",
                        HSN: row[8] ? String(row[8]) : "",
                        MIKCode: row[9] ? String(row[9]) : "",
                        GST: row[12] ?? 0,
                        MRP: row[7] ?? 0,
                        Category: row[11] ?? 0,
                    };
                });

            setFileData(formattedData);
        };

        reader.readAsArrayBuffer(file);
    };

    const handleImport = async (event) => {
        setIsLoading(true);

        const CreateData = {
            CompanyId: companyId,
            BranchId: branchId,
            CreatedBy: userId,
            ItemImport: fileData
        };

        const url = 'ItemImportApi';
        const baseURL = 'https://mga-api.off-ease.com/api/';
        //const baseURL = 'https://localhost:7069/api/';

        try {
            const response = await axios.post(
                `${baseURL}${url}`,
                CreateData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            // Handle response here if needed
        } catch (error) {
            console.error('Error importing data:', error);
            // Optionally, handle the error state or notify the user
        } finally {
            setIsLoading(false);
            console.log('Item Imported !');
        }
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
        setSearchQuery('');
    };

    const filteredData = data.filter((item) =>
        item.Code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.Name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const Row = ({ index, style }) => {
        const item = filteredData[index];

        return (
            <TableRow key={item.id} style={style} className={item.IsActive ? '' : 'inactiveRow'}
                sx={{
                    '&:hover': {
                        backgroundColor: '#B2DFDB', // Change color on hover
                        cursor: 'pointer',
                    },
                }}
            >
                <TableCell sx={{ backgroundColor: 'whitesmoke', borderBottom: '1px solid #C0C0C0', padding: '3px', width: '5%', fontSize: '12px' }}>{index + 1}</TableCell>
                {/* <TableCell sx={{ backgroundColor: 'whitesmoke', borderBottom: '1px solid #C0C0C0', padding: '3px', width: '11%', fontSize: '11px' }}>{item.CategoryName}</TableCell> */}
                <TableCell sx={{ backgroundColor: 'whitesmoke', borderBottom: '1px solid #C0C0C0', padding: '3px', width: '5%', fontSize: '12px' }}></TableCell>
                <TableCell sx={{ backgroundColor: 'whitesmoke', borderBottom: '1px solid #C0C0C0', padding: '3px', width: '20%', fontSize: '11px' }}>{item.Name}</TableCell>
                <TableCell sx={{ backgroundColor: 'whitesmoke', borderBottom: '1px solid #C0C0C0', padding: '3px', width: '10%', fontSize: '12px' }}>{item.HSNCode}</TableCell>
                <TableCell sx={{ backgroundColor: 'whitesmoke', borderBottom: '1px solid #C0C0C0', padding: '3px', width: '10%', fontSize: '12px' }}>{Number(item.CGSTRate) + Number(item.SGSTRate)}%</TableCell>
                <TableCell sx={{ backgroundColor: 'whitesmoke', borderBottom: '1px solid #C0C0C0', padding: '3px', width: '10%', fontSize: '12px' }}>{item.MRP}</TableCell>
                <TableCell sx={{ backgroundColor: 'whitesmoke', borderBottom: '1px solid #C0C0C0', padding: '3px', width: '10%', fontSize: '12px' }}>{item.SaleRate}</TableCell>
                <TableCell sx={{ backgroundColor: 'whitesmoke', borderBottom: '1px solid #C0C0C0', padding: '3px', width: '10%', fontSize: '12px' }}></TableCell>
                <TableCell sx={{ backgroundColor: 'whitesmoke', borderBottom: '1px solid #C0C0C0', padding: '3px', width: '10%', fontSize: '12px' }}>{item.PurchaseRate}</TableCell>
                <TableCell sx={{ backgroundColor: 'whitesmoke', borderBottom: '1px solid #C0C0C0', padding: '3px', width: '15%', fontSize: '12px' }}>
                    {hasEditMasterPermission && (
                        <Link to={`/itemEdit/${item.Id}`}>
                            <IconButton color="primary">
                                <EditIcon style={{ fontSize: '19px' }} />
                            </IconButton>
                        </Link>
                    )}
                    {hasDeleteMasterPermission && (
                        <IconButton onClick={() => openConfirmDialog(item.Id)} color="error">
                            <DeleteIcon style={{ fontSize: '19px' }} />
                        </IconButton>
                    )}
                </TableCell>
                <TableCell sx={{ borderBottom: '1px solid #C0C0C0', padding: '3px', width: '10%', fontSize: '12px' }}></TableCell>
            </TableRow>
        );
    };

    return (
        <div className="item-index-container">
            <div className="w-full max-w-sm container mt-2 mx-auto">
                <div className='w-full mb-2 flex' style={{ justifyContent: 'space-between', marginTop: '-20px' }}>
                    <h6 className='block uppercase tracking-wide text-black productHead flex-grow linkcolor' style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif', textAlign: 'left', marginLeft: '-20px' }}>Item </h6>
                    <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                        <input
                            className="shadow text-gray-700 leading-tight"
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearch}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                            style={{
                                width: isFocused ? '270px' : '200px',
                                height: '30px',
                                border: '1px solid #ccc',
                                borderRadius: '15px',
                                padding: '0 20px',
                                transition: 'width 0.4s ease-in-out, border-color 0.3s ease-in-out',
                                outline: 'none',
                                borderColor: isFocused ? 'darkcyan' : '#ccc',
                                boxShadow: isFocused ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
                                marginTop: '15px',
                            }}
                        />
                    </div>
                   
                    {hasCreateMasterPermission && (
                        <Tooltip
                            title="Create New Item" arrow>
                            <Button
                                style={{
                                    backgroundColor: '#3F4F44', color: 'white', height: '26px', marginRight: '8px',
                                    marginLeft: '5px', marginTop: '15px'
                                }}
                                component={Link}
                                to="/ItemCreate"
                                className="linkcolor"
                            >
                                <span style={{ fontSize: '13px' }}>Create</span>
                            </Button>
                        </Tooltip>
                    )}
                </div>
            </div>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead sx={{ background: 'linear-gradient(to right, #484743, #27445D)', borderRadius: '5px', padding: '3px' }}>
                        <TableRow>
                            <TableCell>
                                <span style={{ marginLeft: '-5px', color: 'white', fontWeight: 'bolder', fontSize: '12px', padding: '3px' }}>Sl No</span>
                                {/* <span style={{ marginLeft: '35px', color: 'white', fontWeight: 'bolder', fontSize: '12px', padding: '3px' }}>Category</span> */}
                                <span style={{ marginLeft: '20px', color: 'white', fontWeight: 'bolder', fontSize: '12px', padding: '3px' }}></span>
                                <span style={{ marginLeft: '70px', color: 'white', fontWeight: 'bolder', fontSize: '12px', padding: '3px' }}>Name</span>
                                <span style={{ marginLeft: '240px', color: 'white', fontWeight: 'bolder', fontSize: '12px', padding: '3px' }}>HSN</span>
                                <span style={{ marginLeft: '100px', color: 'white', fontWeight: 'bolder', fontSize: '12px', padding: '3px' }}>GST</span>
                                <span style={{ marginLeft: '105px', color: 'white', fontWeight: 'bolder', fontSize: '12px', padding: '3px' }}>MRP</span>
                                <span style={{ marginLeft: '110px', color: 'white', fontWeight: 'bolder', fontSize: '12px', padding: '3px' }}>Sale Price</span>
                                <span style={{ marginLeft: '115px', color: 'white', fontWeight: 'bolder', fontSize: '12px', padding: '3px' }}></span>
                                <span style={{ marginLeft: '85px', color: 'white', fontWeight: 'bolder', fontSize: '12px', padding: '3px' }}>Purchase Rate</span>
                                <span style={{ marginLeft: '110px', color: 'white', fontWeight: 'bolder', fontSize: '12px', padding: '3px' }}></span>
                            </TableCell>

                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ backgroundColor: '#C3E9E4' }}>
                        <List
                            height={500}
                            itemCount={filteredData.length}
                            itemSize={60}
                            width="100%"
                        >
                            {Row}
                        </List>
                    </TableBody>
                </Table>
            </TableContainer>
            <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to delete this item?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConfirmDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} color="secondary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={importDialogOpen} onClose={closeImportDialog}>
                <DialogTitle>Import Items</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Upload an Excel file to import items. Ensure the file format is correct.
                    </DialogContentText>
                    <input type="file" onChange={handleFileChange} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeImportDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleImport} color="secondary" disabled={isLoading}>
                        {isLoading ? <CircularProgress size={24} /> : 'Import'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
export default ItemIndex;